import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { AlertController, ToastController } from "@ionic/angular";
import { NavigationHelper } from "../../../app/helpers/navigation";
import { ErrorModalService } from "../../../app/services/error-modal.service";

@Injectable({
	providedIn: 'root',
})
export class PottyNotesAlertController {
	alertText: string;

	constructor(private alertController: AlertController,
		private navigationHelper: NavigationHelper,
		private router: Router,
		private toastController: ToastController,
		private errorModalService: ErrorModalService
	) { }

	displayGenericFailure() {
		this.errorModalService.openErrorModal();
	}

	displayExceededDiaryAmountAlert() {
		this.alertController.create({
			header: 'Hold on..',
			subHeader: 'You already have a bathroom guestbook.',
			message: `Currently, only one guestbook is allowed.\nThis will change in a future release.`,
			buttons: [
				{
					text: 'Go to my guestbook',
					handler: () => {
						this.navigationHelper.redirectToDiaryOwnerNotes(this.router)
					}
				}
			]
		}).then(res => { res.present() });
	}

	displayGuestbookNotFound() {
		this.alertController.create({
			header: 'Hmmm..',
			subHeader: 'Looks like we couldn\'t find that guestbook.',
			message: 'Maybe the guestbook doesn\'t exist anymore?',
			buttons: ['OK'],
			cssClass: 'custom-alert'
		}).then(res => { res.present() });
	}

	displayGiftNotFound() {
		this.alertController.create({
			header: 'Hmmm..',
			subHeader: 'Looks like we couldn\'t find that gift.',
			message: 'Please contact support by emailing admin@pottynotes.com',
			buttons: ['OK'],
			cssClass: 'custom-alert'
		}).then(res => { res.present() });
	}

	displayNoteNotFound() {
		this.alertController.create({
			header: 'Hmmm..',
			subHeader: 'Looks like we couldn\'t find that note.',
			message: 'Maybe the note doesn\'t exist anymore?',
			buttons: ['OK'],
			cssClass: 'custom-alert'
		}).then(res => { res.present() });
	}

	displayDeleteNoteSuccess(router: Router) {
		this.toastController.create({
			header: 'Deleted!',
			position: 'top',
			icon: 'success', // Could wire this up.
			duration: 3000,
			cssClass: 'custom-alert'
		}).then((toast) => {
			toast.present();
		});
	}

	displayDeleteNoteFailure() {
		this.toastController.create({
			header: 'Oops, something went wrong. Please try again.',
			position: 'middle',
			icon: 'failure',
			duration: 3000,
			cssClass: 'custom-alert'
		}).then((toast) => {
			toast.present();
		});
	}

	displayForgotPasswordFailure() {
		this.toastController.create({
			header: 'Oops, something went wrong. Please try again.',
			position: 'middle',
			icon: 'failure', // Could wire this up.
			duration: 3000,
			cssClass: 'custom-alert'
		}).then((toast) => {
			toast.present();
		});
	}

	displayConsumedOrExpiredForgotPasswordTokenFailure() {
		this.toastController.create({
			header: 'This link has been used or expired.',
			position: 'middle',
			icon: 'failure', // Could wire this up.
			duration: 5000,
			cssClass: 'custom-alert'
		}).then((toast) => {
			toast.present();
		});
	}

	displayGuestbookNotFoundForPin() {
		this.alertController.create({
			header: 'Hmmm..',
			subHeader: 'Looks like we couldn\'t find that guestbook when validating the pin.',
			message: 'Maybe the guestbook doesn\'t exist anymore?',
			buttons: ['OK'],
			cssClass: 'custom-alert'
		}).then(res => { res.present() });
	}

	displayPinFailure() {
		this.alertController.create({
			header: 'Hmmm..',
			subHeader: 'That is not the correct pin.',
			message: 'Please try again.',
			buttons: ['OK']
		}).then(res => { res.present() });
	}

	displayAlreadyClaimedFailure() {
		this.alertController.create({
			header: 'Hmmm...',
			subHeader: 'This guestbook has already been claimed.',
			message: 'Please contact support for assistance.',
			buttons: ['OK'],
			cssClass: 'custom-alert'
		}).then(res => { res.present() });
	}

	displayGetDiaryFailureInSettings() {
		this.alertController.create({
			header: 'Hmmm...',
			subHeader: 'We couldn\'t locate your guestbook.',
			message: 'Please try again.',
			buttons: ['OK'],
			cssClass: 'custom-alert'
		}).then(res => { res.present() });
	}

	displayDuplicateEmailOnCreate() {
		this.alertController.create({
			header: 'Wait a second',
			subHeader: 'We couldn\'t create that for you.',
			message: `The email you tried to update already exists, please use a different email.`,
			buttons: ['OK'],
			cssClass: 'custom-alert'
		}).then(res => { res.present() });
	}

	displayDuplicateEmailOnUpdate() {
		this.alertController.create({
			header: 'Wait a second',
			subHeader: 'We couldn\'t update that for you.',
			message: `The email you tried to update already exists, please use a different email.`,
			buttons: ['OK'],
			cssClass: 'custom-alert'
		}).then(res => { res.present() });
	}

	insufficientMetadataForUpdateOperation() {
		this.toastController.create({
			header: 'Need something to update.',
			position: 'top',
			duration: 3000,
			cssClass: 'custom-alert'
		}).then((toast) => {
			toast.present();
		});
	}

	displayCantProceedFailure() {
		this.alertController.create({
			header: 'Wait a second',
			subHeader: 'Please provide all required values.',
			message: '',
			buttons: ['OK'],
			cssClass: 'custom-alert'
		}).then(res => { res.present() });
	}

	displayQrCodeAutoLoginFailure() {
		this.alertController.create({
			header: 'Hmmm..',
			subHeader: 'We couldn\'t authorize you to view this guestbook.',
			message: `Try manually inputting the pin instead.`,
			buttons: [
				{
					text: 'Enter pin manually',
					handler: () => {
						this.navigationHelper.redirectToEnterPinPage(this.router)
					}
				}
			],
			cssClass: 'custom-alert'
		}).then(res => { res.present() });
	}

	displayForbiddenDiaryOnRead() {
		this.alertController.create({
			header: 'Hmmm..',
			subHeader: 'Something is not quite right.',
			message: 'You are not authorized to view this guestbook.',
			buttons: [
				{
					text: 'OK',
					handler: () => {
						this.navigationHelper.redirectToSignInPage(this.router)
					}
				}
			],
			cssClass: 'custom-alert'
		}).then(res => { res.present() });
	}

	displayForbiddenAccount() {
		this.alertController.create({
			header: 'Hmmm..',
			subHeader: 'Something is not quite right.',
			message: 'You are not authorized to access this account.',
			buttons: [
				{
					text: 'OK',
					handler: () => {
						this.navigationHelper.redirectToSignInPage(this.router)
					}
				}
			],
			cssClass: 'custom-alert'
		}).then(res => { res.present() });
	}

	displayForbiddenOnNoteSubmit() {
		this.alertController.create({
			header: 'Hmmm..',
			subHeader: 'Something is not quite right.',
			message: 'You are not authorized to submit notes to this guestbook.',
			buttons: [
				{
					text: 'Enter pin manually',
					handler: () => {
						this.navigationHelper.redirectToEnterPinPage(this.router)
					}
				}
			],
			cssClass: 'custom-alert'
		}).then(res => { res.present() });
	}

	displayForbiddenNote() {
		this.alertController.create({
			header: 'Hmmm..',
			subHeader: 'Something is not quite right.',
			message: 'You are not authorized to view this note.',
			buttons: ['OK'],
			cssClass: 'custom-alert'
		}).then(res => { res.present() });
	}

	displayAuthorizationFailure() {
		this.alertController.create({
			header: 'Hmmm..',
			subHeader: 'The provided credentials were not quite right.',
			message: 'Please try again.',
			buttons: ['OK'],
			cssClass: 'custom-alert'
		}).then(res => { res.present() });
	}

	displaySubmitNoteFailure() {
		this.alertController.create({
			header: 'Hmmm..',
			subHeader: 'Something went wrong.',
			message: 'Please try submitting your note again.',
			buttons: ['OK'],
			cssClass: 'custom-alert'
		}).then(res => { res.present() });
	}

	displaySongRetrievalFailure() {
		this.alertController.create({
			header: 'Hmmm..',
			subHeader: 'We couldn\'t get a list of songs for you.',
			message: 'Please try again.',
			buttons: ['OK'],
			cssClass: 'custom-alert'
		}).then(res => { res.present() });
	}

	displayGetQuestionsFailure() {
		this.alertController.create({
			header: 'Hmmm..',
			subHeader: 'We couldn\'t get questions for you.',
			message: 'Please try again.',
			buttons: ['OK'],
			cssClass: 'custom-alert'
		}).then(res => { res.present() });
	}

	displaySuccessfulSettingsUpdate() {
		this.toastController.create({
			header: 'Success!',
			position: 'top',
			icon: 'success',
			duration: 3000,
			cssClass: 'custom-alert'
		}).then((toast) => {
			toast.present();
		});
	}

	displayFailedSettingsUpdate() {
		this.toastController.create({
			header: 'Failed to update settings.',
			message: 'Please try again.',
			position: 'middle',
			icon: 'failure',
			duration: 3000,
			cssClass: 'custom-alert'
		}).then((toast) => {
			toast.present();
		});
	}

	displayFailedPasswordUpdate() {
		this.toastController.create({
			header: 'Failed to update password.',
			message: 'Please try again.',
			position: 'middle',
			icon: 'failure',
			duration: 3000,
			cssClass: 'custom-alert'
		}).then((toast) => {
			toast.present();
		});
	}

	displayFailedSettingsUpdateIncorrectCurrentPassword() {
		this.toastController.create({
			header: 'Incorrect current password.',
			position: 'middle',
			icon: 'failure',
			cssClass: 'custom-alert'
		}).then((toast) => {
			toast.present();
		});
	}
}
